import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Wrench, Save, Trash2, TrendingDown } from "lucide-react";
import { t } from "@/lib/i18n";

const dollars = (n) =>
  n == null || n === "" ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const MitigationCard = ({ readings, onChanged }) => {
  const [mit, setMit] = useState(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    completed_date: new Date().toISOString().slice(0, 10),
    contractor: "",
    system_type: t("Sub-slab depressurization"),
    cost_usd: "",
    pre_reading_id: "",
    post_reading_id: "",
    notes: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/mitigation");
      setMit(data);
      if (data) {
        setForm({
          completed_date: data.completed_date,
          contractor: data.contractor || "",
          system_type: data.system_type || "",
          cost_usd: data.cost_usd ?? "",
          pre_reading_id: data.pre_reading_id || "",
          post_reading_id: data.post_reading_id || "",
          notes: data.notes || "",
        });
      }
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const preOpts = readings.filter((r) => r.kind !== "post_mitigation");
  const postOpts = readings.filter((r) => r.kind === "post_mitigation");

  const preR = mit ? readings.find((r) => r.id === mit.pre_reading_id) : null;
  const postR = mit ? readings.find((r) => r.id === mit.post_reading_id) : null;
  const reduction =
    preR && postR && preR.pci_l > 0
      ? (((preR.pci_l - postR.pci_l) / preR.pci_l) * 100).toFixed(1)
      : null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        cost_usd: form.cost_usd === "" ? null : Number(form.cost_usd),
        pre_reading_id: form.pre_reading_id || null,
        post_reading_id: form.post_reading_id || null,
      };
      const { data } = await api.put("/mitigation", payload);
      setMit(data);
      setEditing(false);
      onChanged?.();
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!confirm("Remove this mitigation record?")) return;
    await api.delete("/mitigation");
    setMit(null);
    onChanged?.();
  };

  if (!editing && !mit) {
    return (
      <div className="card-flat" data-testid="mitigation-card">
        <div className="flex items-center gap-2 mb-2">
          <Wrench size={16} className="text-[#2F524A]" />
          <h3 className="font-serif text-xl">{t("Mitigation")}</h3>
        </div>
        <p className="text-sm text-[#5C6661]">
          {t("Record your mitigation once complete — contractor, cost, and pre/post readings.")}
        </p>
        <button
          onClick={() => setEditing(true)}
          className="btn-primary mt-4 !py-2 !px-4 text-sm inline-flex items-center gap-2"
          data-testid="mitigation-add-button"
        >
          {t("Add mitigation record")}
        </button>
      </div>
    );
  }

  if (!editing && mit) {
    return (
      <div className="card-flat" data-testid="mitigation-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wrench size={16} className="text-[#2F524A]" />
            <h3 className="font-serif text-xl">{t("Mitigation")}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(true)} className="text-xs text-[#2F524A] hover:underline" data-testid="mitigation-edit-button">{t("Edit")}</button>
            <button onClick={del} className="text-[#5C6661] hover:text-[#9B2C2C] p-1" aria-label={t("Delete")} data-testid="mitigation-delete-button"><Trash2 size={14} /></button>
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
          <dt className="text-[#5C6661]">{t("Completed")}</dt><dd className="text-[#1C201F]" data-testid="mitigation-completed-date">{mit.completed_date}</dd>
          <dt className="text-[#5C6661]">{t("Contractor")}</dt><dd className="text-[#1C201F]">{mit.contractor}</dd>
          <dt className="text-[#5C6661]">{t("System")}</dt><dd className="text-[#1C201F]">{mit.system_type ? t(mit.system_type) : "—"}</dd>
          <dt className="text-[#5C6661]">{t("Cost")}</dt><dd className="text-[#1C201F]">{dollars(mit.cost_usd)}</dd>
        </dl>

        {preR && postR && (
          <div className="mt-5 rounded-2xl border border-[#E8ECEA] p-4 bg-[#F4F7F6]" data-testid="mitigation-before-after">
            <p className="eyebrow flex items-center gap-1"><TrendingDown size={12} />{" "}{t("Before & After")}</p>
            <div className="mt-3 flex items-end justify-between gap-4">
              <div>
                <p className="text-xs text-[#5C6661]">{t("Pre")}</p>
                <p className="font-serif text-3xl text-[#1C201F]">{preR.pci_l.toFixed(1)}</p>
                <p className="text-[10px] text-[#5C6661]">{t("pCi/L ·")}{" "}{preR.reading_date}</p>
              </div>
              <div className="flex-1 relative h-6">
                <div className="absolute inset-y-1/2 -translate-y-1/2 left-0 right-0 h-1 bg-[#E8ECEA] rounded-full">
                  <div
                    className="h-1 rounded-full"
                    style={{ background: "#6B9080", width: `${Math.max(4, 100 - Math.min(100, (postR.pci_l / preR.pci_l) * 100))}%` }}
                  />
                </div>
                <div className="absolute inset-y-1/2 -translate-y-1/2 -mt-1 left-1/2 -translate-x-1/2 text-xs font-semibold text-[#2F524A]" data-testid="mitigation-reduction-pct">
                  {reduction}% ↓
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#5C6661]">{t("Post")}</p>
                <p className="font-serif text-3xl text-[#6B9080]">{postR.pci_l.toFixed(1)}</p>
                <p className="text-[10px] text-[#5C6661]">{t("pCi/L ·")}{" "}{postR.reading_date}</p>
              </div>
            </div>
          </div>
        )}
        {mit.notes && <p className="text-xs text-[#5C6661] mt-4">{t("Notes:")}{" "}{mit.notes}</p>}
      </div>
    );
  }

  return (
    <form onSubmit={save} className="card-flat space-y-4" data-testid="mitigation-form">
      <div className="flex items-center gap-2">
        <Wrench size={16} className="text-[#2F524A]" />
        <h3 className="font-serif text-xl">{mit ? t("Edit mitigation") : t("Add mitigation record")}</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="eyebrow block mb-2">{t("Date completed *")}</label>
          <input type="date" required max={new Date().toISOString().slice(0, 10)}
            className="input-field" value={form.completed_date}
            onChange={(e) => setForm((f) => ({ ...f, completed_date: e.target.value }))}
            data-testid="mitigation-date-input" />
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Contractor *")}</label>
          <input type="text" required maxLength={120} className="input-field"
            value={form.contractor}
            onChange={(e) => setForm((f) => ({ ...f, contractor: e.target.value }))}
            placeholder={t("Radon Away, Inc.")}
            data-testid="mitigation-contractor-input" />
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("System type")}</label>
          <input type="text" maxLength={120} className="input-field" value={form.system_type}
            onChange={(e) => setForm((f) => ({ ...f, system_type: e.target.value }))}
            placeholder={t("Sub-slab depressurization")} />
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Cost (USD)")}</label>
          <input type="number" min={0} max={100000} className="input-field"
            value={form.cost_usd}
            onChange={(e) => setForm((f) => ({ ...f, cost_usd: e.target.value }))}
            placeholder="1250"
            data-testid="mitigation-cost-input" />
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Pre-mitigation reading")}</label>
          <select className="input-field" value={form.pre_reading_id}
            onChange={(e) => setForm((f) => ({ ...f, pre_reading_id: e.target.value }))}
            data-testid="mitigation-pre-select">
            <option value="">{t("— none —")}</option>
            {preOpts.map((r) => (
              <option key={r.id} value={r.id}>{r.reading_date} · {r.pci_l.toFixed(1)} pCi/L</option>
            ))}
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Post-mitigation reading")}</label>
          <select className="input-field" value={form.post_reading_id}
            onChange={(e) => setForm((f) => ({ ...f, post_reading_id: e.target.value }))}
            data-testid="mitigation-post-select">
            <option value="">{t("— none —")}</option>
            {postOpts.map((r) => (
              <option key={r.id} value={r.id}>{r.reading_date} · {r.pci_l.toFixed(1)} pCi/L</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="eyebrow block mb-2">{t("Notes (optional)")}</label>
          <input type="text" className="input-field" maxLength={500} value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder={t("Penetrations, warranty, etc.")} />
        </div>
      </div>

      {error && <div className="text-sm text-[#9B2C2C] bg-[#FCEDED] border border-[#F5C2C2] rounded-lg px-3 py-2">{error}</div>}

      <div className="flex items-center justify-end gap-3">
        {mit && <button type="button" onClick={() => setEditing(false)} className="btn-secondary !py-2 !px-4 text-sm">{t("Cancel")}</button>}
        <button type="submit" disabled={saving}
          className="btn-primary !py-2 !px-5 text-sm inline-flex items-center gap-2"
          data-testid="mitigation-save-button">
          <Save size={14} /> {saving ? t("Saving…") : t("Save mitigation")}
        </button>
      </div>
    </form>
  );
};

export default MitigationCard;
